/**
 * LiveTranscriptPanel - scrollable live transcript column.
 *
 * Renders a list of transcript segments with speaker label and session-offset
 * timestamp. Supports partial (interim) and finalized segments - partials are
 * displayed immediately as they arrive and styled distinctly until finalized.
 *
 * Auto-scrolls to the latest entry when new segments are appended. Scroll is
 * region-level (the panel body scrolls, not the page).
 */

import * as React from 'react';
import type { LiveMeetingStatus, TranscriptSegment } from '../types/live-meeting';

export interface LiveTranscriptPanelProps {
  segments: TranscriptSegment[];
  status: LiveMeetingStatus;
  hasMoreTranscript?: boolean;
  isLoadingOlderTranscript?: boolean;
  onLoadOlderTranscript?: () => void;
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export const LiveTranscriptPanel = React.memo(function LiveTranscriptPanel({
  segments,
  status,
  hasMoreTranscript = false,
  isLoadingOlderTranscript = false,
  onLoadOlderTranscript,
}: LiveTranscriptPanelProps) {
  const panelBodyRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = React.useRef(0);
  // Set to true before triggering a load-older fetch; cleared inside the
  // auto-scroll useEffect so it fires once and prevents scrolling to bottom.
  const suppressAutoScrollRef = React.useRef(false);

  // Auto-scroll to bottom on new live segments.
  // Suppressed for one render cycle after a load-older completes (older entries
  // are prepended, not appended - we don't want to jump to the bottom).
  React.useEffect(() => {
    // Guard first: while loading is in progress the effect can fire because
    // isLoadingOlderTranscript is a dep - don't consume suppressAutoScrollRef here.
    if (isLoadingOlderTranscript) return;
    if (suppressAutoScrollRef.current) {
      suppressAutoScrollRef.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [segments.length, isLoadingOlderTranscript]);

  // After older segments are prepended, anchor the scroll so the user stays
  // at the same visual position (new content appears above, nothing jumps).
  React.useLayoutEffect(() => {
    if (isLoadingOlderTranscript) return;
    const el = panelBodyRef.current;
    if (el && prevScrollHeightRef.current > 0) {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [segments.length, isLoadingOlderTranscript]);

  const handleLoadEarlier = () => {
    const el = panelBodyRef.current;
    if (el) prevScrollHeightRef.current = el.scrollHeight;
    suppressAutoScrollRef.current = true;
    onLoadOlderTranscript?.();
  };

  return (
    <div ref={panelBodyRef} className="lm-transcript__panel-body">
      {isLoadingOlderTranscript ? (
        <div
          className="lm-transcript__load-earlier lm-transcript__load-earlier--loading"
          aria-live="polite"
          aria-busy="true"
        >
          Loading earlier messages&hellip;
        </div>
      ) : hasMoreTranscript ? (
        <div className="lm-transcript__load-earlier">
          <button
            type="button"
            className="lm-transcript__load-earlier-btn"
            onClick={handleLoadEarlier}
          >
            Load earlier messages
          </button>
        </div>
      ) : null}
      {segments.length === 0 ? (
        <p className="lm-transcript__empty">
          {status === 'active' || status === 'waiting'
            ? 'Waiting for transcript…'
            : status === 'ended'
              ? 'Meeting ended. No transcript captured.'
              : 'Connecting…'}
        </p>
      ) : (
        <ol className="lm-transcript__list" aria-live="polite" aria-label="Live transcript">
          {segments.map((seg, i) => {
            // System events (participant joined/left) render as a centered timeline marker.
            if (seg.isSystemEvent) {
              return (
                <li key={seg.segmentId} className="lm-transcript__system-event" role="note">
                  <span className="lm-transcript__system-event-text">{seg.text}</span>
                </li>
              );
            }

            const prev = segments[i - 1];
            // Don't run speaker-change detection against a preceding system event entry.
            const prevSpeechSeg = prev && !prev.isSystemEvent ? prev : undefined;
            const showSpeaker = !prevSpeechSeg || prevSpeechSeg.speakerId !== seg.speakerId;
            const time = formatTime(seg.startedAt);
            const showTime = !prevSpeechSeg || formatTime(prevSpeechSeg.startedAt) !== time;
            return (
              <React.Fragment key={seg.segmentId}>
                {showSpeaker && (
                  <li
                    className="lm-transcript__speaker-header"
                    role="presentation"
                    aria-hidden="true"
                  >
                    <span className="lm-transcript__speaker">{seg.speakerName}</span>
                    {seg.speakerRole && (
                      <span className="lm-transcript__speaker-role">{seg.speakerRole}</span>
                    )}
                  </li>
                )}
                <li
                  className={`lm-transcript__chunk lm-transcript__chunk--${seg.status}`}
                  aria-label={`${seg.speakerName}: ${seg.text}`}
                >
                  <p className="lm-transcript__text">{seg.text}</p>
                  {showTime && <span className="lm-transcript__timestamp">{time}</span>}
                </li>
              </React.Fragment>
            );
          })}
        </ol>
      )}
      <div ref={bottomRef} aria-hidden="true" />
    </div>
  );
});
